import { useState, useRef, useEffect } from 'react';
import {
  CHAT_BUTTON_ID,
  CHAT_OPEN_EVENT,
  getChatApiUrl,
} from '@/lib/chat';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: string;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
}

interface SpeechRecognitionEvent {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
  isFinal: boolean;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

interface SpeechRecognitionErrorEvent {
  error: string;
  message: string;
}

declare var webkitSpeechRecognition: {
  new (): SpeechRecognition;
};

const CHAT_MESSAGES_EXPIRY_HOURS = 24;
const CHAT_MESSAGES_STORAGE_KEY = 'ahealth_chatbot_messages';
const CHAT_MESSAGES_TIMESTAMP_KEY = 'ahealth_chatbot_messages_timestamp';
const CHAT_SESSION_STORAGE_KEY = 'ahealth_chatbot_session_id';

const WELCOME_MESSAGE: Message = {
  id: '1',
  text: "Hello! I'm here to help with questions about Alliance Health Physicians. How can I assist you today?",
  sender: 'bot',
  timestamp: new Date().toISOString(),
};

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>(() => {
    try {
      const storedMessages = localStorage.getItem(CHAT_MESSAGES_STORAGE_KEY);
      const storedTimestamp = localStorage.getItem(CHAT_MESSAGES_TIMESTAMP_KEY);

      if (storedMessages && storedTimestamp) {
        const timestamp = parseInt(storedTimestamp, 10);
        const now = Date.now();
        const expiryTime = CHAT_MESSAGES_EXPIRY_HOURS * 60 * 60 * 1000;

        if (now - timestamp < expiryTime) {
          const parsedMessages = JSON.parse(storedMessages);
          if (parsedMessages?.length > 0) {
            return parsedMessages;
          }
        } else {
          localStorage.removeItem(CHAT_MESSAGES_STORAGE_KEY);
          localStorage.removeItem(CHAT_MESSAGES_TIMESTAMP_KEY);
        }
      }
    } catch (error) {
      console.error('Error loading chat messages from localStorage:', error);
    }

    return [{ ...WELCOME_MESSAGE, timestamp: new Date().toISOString() }];
  });

  const hasUserMessage = messages.some((msg) => msg.sender === 'user');

  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const recognitionRef = useRef<SpeechRecognition | null>(null);

  const [sessionId, setSessionId] = useState<string>(() => {
    const storedSessionId = localStorage.getItem(CHAT_SESSION_STORAGE_KEY);
    const storedTimestamp = localStorage.getItem(CHAT_MESSAGES_TIMESTAMP_KEY);

    if (storedTimestamp) {
      const timestamp = parseInt(storedTimestamp, 10);
      const now = Date.now();
      const expiryTime = CHAT_MESSAGES_EXPIRY_HOURS * 60 * 60 * 1000;

      if (now - timestamp >= expiryTime) {
        const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem(CHAT_SESSION_STORAGE_KEY, newSessionId);
        localStorage.removeItem(CHAT_MESSAGES_STORAGE_KEY);
        localStorage.removeItem(CHAT_MESSAGES_TIMESTAMP_KEY);
        return newSessionId;
      }
    }

    if (storedSessionId) {
      return storedSessionId;
    }

    const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem(CHAT_SESSION_STORAGE_KEY, newSessionId);
    return newSessionId;
  });

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const apiUrl = getChatApiUrl();

  useEffect(() => {
    const openChat = () => setIsOpen(true);
    window.addEventListener(CHAT_OPEN_EVENT, openChat);
    return () => window.removeEventListener(CHAT_OPEN_EVENT, openChat);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    try {
      if (messages.length > 0) {
        localStorage.setItem(CHAT_MESSAGES_STORAGE_KEY, JSON.stringify(messages));
        localStorage.setItem(CHAT_MESSAGES_TIMESTAMP_KEY, Date.now().toString());
      }
    } catch (error) {
      console.error('Error saving chat messages to localStorage:', error);
    }
  }, [messages]);

  useEffect(() => {
    if (typeof window !== 'undefined' && ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
      const SpeechRecognition =
        (window as Window & { SpeechRecognition?: typeof webkitSpeechRecognition; webkitSpeechRecognition?: typeof webkitSpeechRecognition }).SpeechRecognition ||
        (window as Window & { webkitSpeechRecognition?: typeof webkitSpeechRecognition }).webkitSpeechRecognition;
      if (!SpeechRecognition) return;

      const recognition = new SpeechRecognition();

      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onresult = (event: SpeechRecognitionEvent) => {
        let finalTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += `${transcript} `;
          }
        }

        if (finalTranscript) {
          setInputValue((prev) => {
            const current = prev.trim();
            return current ? `${current} ${finalTranscript.trim()}` : finalTranscript.trim();
          });
        }
      };

      recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
        console.error('Speech recognition error:', event.error);
        setIsRecording(false);

        let errorMessage = 'Speech recognition error occurred.';
        if (event.error === 'no-speech') {
          errorMessage = 'No speech detected. Please try again.';
        } else if (event.error === 'audio-capture') {
          errorMessage = 'Microphone not found or not accessible.';
        } else if (event.error === 'not-allowed') {
          errorMessage = 'Microphone permission denied. Please enable it in your browser settings.';
        }

        const errorMsg: Message = {
          id: (Date.now() + 1).toString(),
          text: errorMessage,
          sender: 'bot',
          timestamp: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, errorMsg]);
      };

      recognition.onend = () => {
        setIsRecording(false);
      };

      recognition.onstart = () => {
        setIsRecording(true);
      };

      recognitionRef.current = recognition;
    }

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch {
          // ignore
        }
      }
    };
  }, []);

  const isSpeechRecognitionSupported = () => {
    return (
      typeof window !== 'undefined' &&
      ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)
    );
  };

  const toggleRecording = () => {
    if (!recognitionRef.current) {
      const errorMsg: Message = {
        id: (Date.now() + 1).toString(),
        text: 'Speech recognition is not supported in your browser. Please use Chrome, Edge, or Safari.',
        sender: 'bot',
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorMsg]);
      return;
    }

    if (isRecording) {
      try {
        recognitionRef.current.stop();
        setIsRecording(false);
      } catch (error) {
        console.error('Error stopping recognition:', error);
        setIsRecording(false);
      }
    } else {
      try {
        if (!inputValue.trim()) {
          setInputValue('');
        }
        recognitionRef.current.start();
      } catch (error) {
        console.error('Error starting recognition:', error);
        const errorMsg: Message = {
          id: (Date.now() + 1).toString(),
          text: 'Could not start recording. Please check your microphone permissions.',
          sender: 'bot',
          timestamp: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, errorMsg]);
      }
    }
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputValue.trim(),
      sender: 'user',
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage.text,
          sessionId,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to get response');
      }

      if (data.sessionId && data.sessionId !== sessionId) {
        setSessionId(data.sessionId);
        localStorage.setItem(CHAT_SESSION_STORAGE_KEY, data.sessionId);
      }

      const botResponse: Message = {
        id: (Date.now() + 1).toString(),
        text: data.reply || "I apologize, but I couldn't process that request.",
        sender: 'bot',
        timestamp: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, botResponse]);
    } catch (error) {
      console.error('Error sending message to chat bot:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: "Sorry, I'm having trouble connecting right now. Please try again later.",
        sender: 'bot',
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const resetConversation = () => {
    const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setSessionId(newSessionId);
    localStorage.setItem(CHAT_SESSION_STORAGE_KEY, newSessionId);
    setMessages([{ ...WELCOME_MESSAGE, timestamp: new Date().toISOString() }]);
    localStorage.removeItem(CHAT_MESSAGES_STORAGE_KEY);
    localStorage.removeItem(CHAT_MESSAGES_TIMESTAMP_KEY);
  };

  return (
    <>
      {!isOpen && (
        <button
          id={CHAT_BUTTON_ID}
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 w-14 h-14 sm:w-16 sm:h-16 bg-primary-navy text-white rounded-full shadow-2xl hover:shadow-primary-teal/40 transition-all duration-300 flex items-center justify-center z-[9999] hover:scale-110 focus:outline-none focus:ring-4 focus:ring-primary-teal/30 border-2 border-primary-teal"
          aria-label="Open chat"
        >
          <i className="ri-message-3-line text-xl sm:text-2xl"></i>
        </button>
      )}

      {isOpen && (
        <div
          onClick={() => setIsOpen(false)}
          className="hidden sm:block fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
          aria-hidden="true"
        />
      )}

      {isOpen && (
        <div
          className="fixed inset-0 sm:inset-auto sm:bottom-6 sm:right-6 sm:w-96 sm:h-[600px] sm:max-w-[calc(100vw-3rem)] sm:max-h-[calc(100vh-3rem)] sm:rounded-2xl bg-gray-50 shadow-2xl flex flex-col z-[9999] sm:border border-gray-200 sm:bg-white"
          onClick={(e) => e.stopPropagation()}
        >
          {!hasUserMessage && (
            <div className="flex-[2] min-h-[50vh] bg-gradient-to-br from-primary-teal to-primary-navy sm:hidden" />
          )}

          <div className="bg-gradient-to-br from-primary-teal to-primary-navy text-white p-4 sm:rounded-t-2xl flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <i className="ri-robot-line text-xl"></i>
              </div>
              <div>
                <h3 className="font-semibold text-lg">Chat Assistant</h3>
                <p className="text-xs text-white/80">Alliance Health Physicians</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={resetConversation}
                className="w-8 h-8 flex items-center justify-center hover:bg-white/20 rounded-full transition-colors"
                aria-label="Start new conversation"
                title="Start new conversation"
              >
                <i className="ri-restart-line text-xl"></i>
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="w-8 h-8 flex items-center justify-center hover:bg-white/20 rounded-full transition-colors"
                aria-label="Close chat"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 min-h-0">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                    message.sender === 'user'
                      ? 'bg-gradient-to-br from-primary-teal to-primary-navy text-white'
                      : 'bg-white text-gray-800 shadow-sm border border-gray-200'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{message.text}</p>
                  <p
                    className={`text-xs mt-1 ${
                      message.sender === 'user' ? 'text-white/80' : 'text-gray-500'
                    }`}
                  >
                    {message.timestamp &&
                      new Date(message.timestamp).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                  </p>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white rounded-2xl px-4 py-2 shadow-sm border border-gray-200">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div
                      className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                      style={{ animationDelay: '0.1s' }}
                    ></div>
                    <div
                      className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                      style={{ animationDelay: '0.2s' }}
                    ></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="p-4 bg-white border-t border-gray-200 sm:rounded-b-2xl flex-shrink-0 safe-area-inset-bottom">
            {isRecording && (
              <div className="mb-2 flex items-center justify-center gap-2 text-red-600 animate-pulse">
                <div className="w-2 h-2 bg-red-600 rounded-full"></div>
                <span className="text-sm font-medium">Recording... Speak now</span>
              </div>
            )}
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={isRecording ? 'Listening...' : 'Type your message...'}
                disabled={isLoading || isRecording}
                className="flex-1 px-4 py-2 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-teal focus:border-primary-teal disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
              {isSpeechRecognitionSupported() && (
                <button
                  onClick={toggleRecording}
                  disabled={isLoading}
                  className={`w-12 h-12 rounded-xl transition-all flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-primary-teal focus:ring-offset-2 ${
                    isRecording
                      ? 'bg-red-500 text-white hover:bg-red-600 animate-pulse'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                  aria-label={isRecording ? 'Stop recording' : 'Start recording'}
                  title={isRecording ? 'Stop recording' : 'Start voice recording'}
                >
                  <i className={`ri-mic${isRecording ? '-fill' : '-line'} text-xl`}></i>
                </button>
              )}
              <button
                onClick={sendMessage}
                disabled={!inputValue.trim() || isLoading || isRecording}
                className="w-12 h-12 bg-gradient-to-br from-primary-teal to-primary-navy text-white rounded-xl hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-primary-teal focus:ring-offset-2"
                aria-label="Send message"
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <i className="ri-send-plane-fill text-xl"></i>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatBot;
